/**
 * Состояние ленты «Движа» — вне экрана, чтобы выдача переживала переходы.
 *
 * ПС §09 / CC §2.6 п. 3: открыл профиль кандидата, вернулся — та же выдача
 * и та же верхняя карточка, а не новый запрос с новыми людьми.
 *
 * Изменение v1.3: единственный режим продукта — `friends` («Движ»); дек
 * хранится на каждую линзу отдельно (BS §11.4), лайк или пропуск убирает
 * человека из всех линз сразу. Выдача устаревает по явной причине:
 * изменились параметры подбора (`invalidate`), пользователь нажал
 * «Повторить», или сменился аккаунт.
 */
import { create } from 'zustand'

import { api, idempotency, unwrap, uuid } from '@shared/api/client'
import { ApiError, isNetworkError } from '@shared/api/errors'
import type { Candidates, LikeResult, Match, ModeName, UserCard } from '@shared/api/types'
import { conversationsRepo, matchesRepo } from '@shared/db'

import { DEFAULT_LENS, type LensId } from './lenses'

/** Единственный режим подбора (изменение v1.3). */
export const DISCOVERY_MODE: ModeName = 'friends'

const PAGE_SIZE = 12
/** Ниже этого остатка подгружаем следующую страницу заранее. */
const PREFETCH_THRESHOLD = 4

export interface DeckState {
  items: UserCard[]
  /** Первая страница запрошена и получена (или отказана) хотя бы раз. */
  loaded: boolean
  loading: boolean
  cursor: string | null
  /** Кандидаты кончились — показываем P-06 с подсказками. */
  exhausted: boolean
  relaxHints: Candidates['relax_hints']
  /** Незаполненные обязательные поля режима → P-07 (`mode_requirements_missing`). */
  missingRequirements: string[]
  /** Подбор выключен (`conflict`, `details.reason = mode_disabled`). */
  modeDisabled: boolean
  error: string | null
  /** id кандидата, по которому сейчас выполняется действие. */
  busyId: string | null
  /** «Движ дня»: верх подборки на момент загрузки, если совпадение в полосе spark. */
  pickId: string | null
}

export const EMPTY_DECK: DeckState = {
  items: [],
  loaded: false,
  loading: false,
  cursor: null,
  exhausted: false,
  relaxHints: [],
  missingRequirements: [],
  modeDisabled: false,
  error: null,
  busyId: null,
  pickId: null,
}

interface DiscoveryStore {
  lens: LensId
  decks: Partial<Record<LensId, DeckState>>
  /** Номер последнего запроса на линзу: только его ответ имеет право менять состояние. */
  generations: Partial<Record<LensId, number>>
  setLens: (lens: LensId) => void
  load: (lens: LensId, reset: boolean) => Promise<void>
  ensure: (lens: LensId) => void
  invalidate: () => void
  like: (userId: string) => Promise<Match | null>
  pass: (userId: string) => Promise<void>
  reset: () => void
}

export const useDiscoveryStore = create<DiscoveryStore>((set, get) => {
  const deckOf = (lens: LensId): DeckState => get().decks[lens] ?? EMPTY_DECK

  const patch = (lens: LensId, changes: Partial<DeckState>): void =>
    set((state) => ({
      decks: { ...state.decks, [lens]: { ...(state.decks[lens] ?? EMPTY_DECK), ...changes } },
    }))

  /** Человек ушёл из выдачи — из всех линз, где он был; текущая при нужде подгружает дальше. */
  const consume = (userId: string): void => {
    set((state) => {
      const decks: Partial<Record<LensId, DeckState>> = {}
      for (const [key, deck] of Object.entries(state.decks) as [LensId, DeckState][]) {
        decks[key] = {
          ...deck,
          items: deck.items.filter((item) => item.id !== userId),
          busyId: deck.busyId === userId ? null : deck.busyId,
        }
      }
      return { decks }
    })
    const lens = get().lens
    const deck = deckOf(lens)
    if (deck.items.length <= PREFETCH_THRESHOLD && deck.cursor) void get().load(lens, false)
  }

  return {
    lens: DEFAULT_LENS,
    decks: {},
    generations: {},

    setLens(lens) {
      set({ lens })
    },

    async load(lens, reset) {
      const deck = deckOf(lens)
      if (!reset && (deck.loading || (!deck.cursor && deck.loaded))) return

      const generation = (get().generations[lens] ?? 0) + 1
      set((state) => ({ generations: { ...state.generations, [lens]: generation } }))
      patch(lens, reset ? { ...EMPTY_DECK, loading: true } : { loading: true, error: null })

      const cursor = reset ? null : deck.cursor

      try {
        const result = unwrap(
          await api.GET('/discovery/candidates', {
            params: {
              query: {
                mode: DISCOVERY_MODE,
                lens,
                limit: PAGE_SIZE,
                ...(cursor ? { cursor } : {}),
              } as never,
            },
          }),
        )

        if (get().generations[lens] !== generation) return

        const current = deckOf(lens)
        const items = reset ? result.items : [...current.items, ...result.items]
        const first = result.items[0]
        patch(lens, {
          items,
          loaded: true,
          loading: false,
          cursor: result.next_cursor,
          exhausted: result.exhausted,
          relaxHints: result.relax_hints,
          missingRequirements: [],
          modeDisabled: false,
          error: null,
          pickId: reset
            ? lens === 'for_you' && first?.vibe?.band === 'spark'
              ? first.id
              : null
            : current.pickId,
        })
      } catch (error) {
        if (get().generations[lens] !== generation) return
        patch(lens, { loaded: true, loading: false, ...describeLoadError(error) })
      }
    },

    ensure(lens) {
      const deck = deckOf(lens)
      if (!deck.loaded && !deck.loading) void get().load(lens, true)
    },

    invalidate() {
      set((state) => ({
        decks: {},
        generations: Object.fromEntries(
          (Object.keys(state.generations) as LensId[]).map((key) => [key, (state.generations[key] ?? 0) + 1]),
        ),
      }))
    },

    async like(userId) {
      const lens = get().lens
      patch(lens, { busyId: userId, error: null })

      try {
        const result: LikeResult = unwrap(
          await api.POST('/users/{userId}/like', {
            params: { path: { userId } },
            body: { mode: DISCOVERY_MODE } as never,
            ...idempotency(uuid()),
          }),
        )

        consume(userId)

        // CC §6.4: при `matched` совпадение сразу пишется в реплику,
        // чтобы экран M-02 открылся без ожидания sync-события.
        if (result.result === 'matched' && result.match) {
          await matchesRepo.put(result.match)
          // Реплика — единственный источник экрана диалога: «Написать» из M-02 ведёт в диалог,
          // о котором sync-дельта `conversation.updated` без записи в реплике ничего не скажет.
          const conversationId = result.conversation_id ?? result.match.conversation_id
          if (conversationId) {
            try {
              const conversation = unwrap(
                await api.GET('/conversations/{conversationId}', { params: { path: { conversationId } } }),
              )
              await conversationsRepo.put(conversation)
            } catch {
              // Диалог догрузится списком при следующем snapshot — переход всё равно возможен позже
            }
          }
          return result.match
        }
        return null
      } catch (error) {
        patch(lens, { busyId: null, error: describeActionError(error) })
        // `user_unavailable` — карточка устарела, убираем её.
        if (error instanceof ApiError && error.code === 'user_unavailable') consume(userId)
        return null
      }
    },

    async pass(userId) {
      const lens = get().lens
      patch(lens, { busyId: userId, error: null })

      try {
        await api.POST('/users/{userId}/pass', {
          params: { path: { userId } },
          body: { mode: DISCOVERY_MODE } as never,
        })
        consume(userId)
      } catch (error) {
        patch(lens, { busyId: null, error: describeActionError(error) })
        if (error instanceof ApiError && error.code === 'user_unavailable') consume(userId)
      }
    },

    reset() {
      set({ lens: DEFAULT_LENS, decks: {}, generations: {} })
    },
  }
})

/* ===================================================================== */
/* Ошибки                                                                 */
/* ===================================================================== */

function describeLoadError(error: unknown): Partial<DeckState> {
  if (error instanceof ApiError) {
    if (error.code === 'mode_requirements_missing') {
      return { missingRequirements: error.details.map((d) => d.field ?? '').filter(Boolean) }
    }
    if (error.code === 'conflict' && error.rateLimitReason() === 'mode_disabled') {
      return { modeDisabled: true }
    }
  }

  return {
    error: isNetworkError(error)
      ? 'Не удалось загрузить подборку: нет соединения.'
      : 'Не удалось загрузить подборку.',
  }
}

function describeActionError(error: unknown): string | null {
  if (isNetworkError(error)) return 'Нет соединения. Действие не выполнено — карточка осталась на месте.'

  if (error instanceof ApiError) {
    switch (error.code) {
      case 'user_unavailable':
        return null // карточка просто исчезает, объяснять нечего
      case 'rate_limited':
        return 'Слишком много действий подряд. Немного подождите.'
      case 'conflict':
        return 'Подбор выключен. Включите его в настройках.'
      default:
        return 'Не удалось выполнить действие.'
    }
  }
  return 'Не удалось выполнить действие.'
}
